// @flow

// NOTE: The location reducer has been DEPRECATED in favor of history. Please
// stop using this reducer and migrate your code.
//
// The location reducer stores the user's history stack as they navigate through
// a given tab/window's session. Every time the user does a soft nav (usually
// through ReactRouter via NavigateLink or withRouter manipulation), their
// history state is updated here.

import findLastIndex from 'lodash/findLastIndex';
import type { Action as PubSubMessageAction } from 'app/common/redux/actions/pubSubMessage';
import type { RouteChangeAction, RouteEnterAction } from 'app/common/redux/actions/history';

export type Entry = {
  pathname: string,
  search?: string,
  previousPath?: string,
  gridItems?: Array<Object> | null,
};

export type State = {
  history: Array<Entry>,
  action?: 'POP' | 'PUSH' | 'REPLACE',
  pathname: string,
  previousPage?: string,
  query?: { [string]: string | Array<String> },
};

let poppedStack = [];

const fullPath = (entry) => (entry ? entry.pathname + (entry.search || '') : '');
const isPath = (entry) => (item) => fullPath(item) === fullPath(entry);

const UPDATERS = {
  POP: (history, entry) => {
    // The browser sends a pop action regardless of whether the user clicked
    // forward or back, so we need to use entry paths in order to differentiate.
    if (poppedStack.length > 0 && fullPath(poppedStack[0]) === fullPath(entry)) {
      // Forward button was pressed.
      poppedStack.shift();
      return history.concat(entry);
    } else {
      // Back button was pressed.
      const lastIndex = findLastIndex(history, isPath(entry));
      poppedStack.unshift(history[lastIndex + 1]);
      return history.slice(0, lastIndex + 1);
    }
  },
  PUSH: (history, entry) => {
    poppedStack = [];
    return history.concat(entry);
  },
  REPLACE: (history, entry) => history.slice(0, history.length - 1).concat(entry),
};

const initialState = { history: [], pathname: '/' };

const location = (
  state: State = initialState,
  action: PubSubMessageAction | RouteChangeAction | RouteEnterAction,
): State => {
  if (action.type === 'PUB_SUB_MESSAGE') {
    const { channel, topic, message = {} } = action.payload;
    if (channel === PCONST.PUBSUB_CHANNEL_SITE && topic === PCONST.PUBSUB_TOPIC_NAVIGATE_STARTED) {
      // Have to explicitly typecast message because pubsub isn't typed
      const pathname: string = ((message: any): Object).url;
      return { ...state, pathname };
    }
  } else if (action.type === 'ROUTE_CHANGE' || action.type === 'ROUTE_ENTER') {
    const { action: method, location, previousRoutePath } = action.payload;
    const { pathname, query, search } = location;
    const history = UPDATERS[method](state.history, {
      pathname,
      search,
      previousPath: previousRoutePath,
    });

    // Set the previousPage value. When the user presses the back button
    // (casuing a pop), the previous page will actually be forward in history.
    if (history.length < state.history.length) {
      // Grab previousPage from the popped stack (forward in history).
      state.previousPage = poppedStack[0].pathname;
    } else if (state.history.length > 0) {
      // The action wasn't a pop. Take the previous page from the back stack.
      state.previousPage = state.history[state.history.length - 1].pathname;
    }

    return { ...state, history, pathname, query, action: method };
  }
  return state;
};

export default location;
